import React from 'react';
import { PageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Factory, CheckCircle, BarChart3, Award } from 'lucide-react';

const BANITUNAPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-16">
      <PageSEO path="/bani-tuna" />
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          
          {/* Header with Extra Top Spacing */}
          <div className="text-center mb-16 mt-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              QRM Stops the Chaos: <span className="text-red-600">BANI/TUNA</span> Framework for Manufacturing
            </h1>
          </div>

          {/* Hook - The Brutal Truth */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
            <h2 className="text-2xl font-bold text-red-800 mb-6">The Brutal Truth</h2>
            <div className="space-y-4 text-lg text-gray-800">
              <p className="font-semibold">Your 30-day lead times are killing you. Customers walk away daily.</p>
              <p className="font-semibold">This chaos costs you €500K yearly. Rush orders, overtime, lost sales.</p>
              <p className="font-semibold text-green-700">We steer on lead time. Cut it to 15 days. Watch profits soar.</p>
            </div>
          </Card>

          {/* BANI Framework */}
          <Card className="p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">BANI: Why Your Factory Breaks</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Brittle */}
              <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="text-xl font-bold text-red-800 mb-3">🔴 Brittle</h3>
                <p className="text-gray-700 mb-2"><strong>Pain:</strong> One supplier fails. Your entire schedule collapses.</p>
                <p className="text-blue-700 mb-2"><strong>QRM:</strong> Build Q-ROC cells with local buffers. No single-point dependency.</p>
                <p className="text-green-700 font-semibold"><strong>Effect:</strong> Lead time stays at 15 days. No more firefighting.</p>
              </div>

              {/* Anxious */}
              <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="text-xl font-bold text-yellow-800 mb-3">😰 Anxious</h3>
                <p className="text-gray-700 mb-2"><strong>Pain:</strong> Priorities shift hourly. Your team chases noise, not flow.</p>
                <p className="text-blue-700 mb-2"><strong>QRM:</strong> Use POLCA cards. Visualize work queues. Prioritize by time, not urgency.</p>
                <p className="text-green-700 font-semibold"><strong>Effect:</strong> Re-planning drops 70%. OTIF climbs from 80% to 95%.</p>
              </div>

              {/* Nonlinear */}
              <div className="p-6 bg-orange-50 border border-orange-200 rounded-lg">
                <h3 className="text-xl font-bold text-orange-800 mb-3">📈 Nonlinear</h3>
                <p className="text-gray-700 mb-2"><strong>Pain:</strong> One jig error halts 3 departments.</p>
                <p className="text-blue-700 mb-2"><strong>QRM:</strong> Daily feedback loops. Cell teams own detection and fix.</p>
                <p className="text-green-700 font-semibold"><strong>Effect:</strong> Problems solved in 2 hours, not 2 days. LT drops 30%.</p>
              </div>

              {/* Incomprehensible */}
              <div className="p-6 bg-purple-50 border border-purple-200 rounded-lg">
                <h3 className="text-xl font-bold text-purple-800 mb-3">❓ Incomprehensible</h3>
                <p className="text-gray-700 mb-2"><strong>Pain:</strong> 12 KPIs. No one knows what matters.</p>
                <p className="text-blue-700 mb-2"><strong>QRM:</strong> Track one thing: lead time. Burn the rest.</p>
                <p className="text-green-700 font-semibold"><strong>Effect:</strong> Clear decisions. WIP drops from 1000 to 600 units.</p>
              </div>
            </div>
          </Card>

          {/* TUNA Framework */}
          <Card className="p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">TUNA: The New Reality Hits Hard</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Turbulent */}
              <div className="p-6 bg-slate-50 border border-slate-200 rounded-lg">
                <h3 className="text-xl font-bold text-slate-800 mb-3">🌪️ Turbulent</h3>
                <p className="text-gray-700 mb-2"><strong>Pain:</strong> Weekly engineering changes wreck your flow.</p>
                <p className="text-blue-700 mb-2"><strong>QRM:</strong> Short decision lines inside cells. Cut approval loops.</p>
                <p className="text-green-700 font-semibold"><strong>Effect:</strong> Re-sequencing takes 10 minutes, not 10 hours.</p>
              </div>

              {/* Uncertain */}
              <div className="p-6 bg-teal-50 border border-teal-200 rounded-lg">
                <h3 className="text-xl font-bold text-teal-800 mb-3">🎲 Uncertain</h3>
                <p className="text-gray-700 mb-2"><strong>Pain:</strong> Lead times swing. Staff fluctuates. Energy costs spike.</p>
                <p className="text-blue-700 mb-2"><strong>QRM:</strong> Plan on time, not capacity. Cap WIP per step.</p>
                <p className="text-green-700 font-semibold"><strong>Effect:</strong> Flow stays stable. External chaos stays outside.</p>
              </div>

              {/* Novel */}
              <div className="p-6 bg-lime-50 border border-lime-200 rounded-lg">
                <h3 className="text-xl font-bold text-lime-800 mb-3">✨ Novel</h3>
                <p className="text-gray-700 mb-2"><strong>Pain:</strong> Client wants a frame you've never built.</p>
                <p className="text-blue-700 mb-2"><strong>QRM:</strong> Multi-skilled cell handles it. Use batches of 5.</p>
                <p className="text-green-700 font-semibold"><strong>Effect:</strong> Learn fast, fail small. WIP stays contained.</p>
              </div>

              {/* Ambiguous */}
              <div className="p-6 bg-rose-50 border border-rose-200 rounded-lg">
                <h3 className="text-xl font-bold text-rose-800 mb-3">🤷 Ambiguous</h3>
                <p className="text-gray-700 mb-2"><strong>Pain:</strong> Overlapping roles. No one owns decisions.</p>
                <p className="text-blue-700 mb-2"><strong>QRM:</strong> Clear ownership inside Q-ROC. One person decides.</p>
                <p className="text-green-700 font-semibold"><strong>Effect:</strong> Decisions in 5 minutes. Lead time drops hard.</p>
              </div>
            </div>
          </Card>

          {/* TUNA Framework */}
          <Card className="p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">TUNA: The New Reality Hits Hard</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Turbulent */}
              <div className="p-6 bg-indigo-50 border border-indigo-200 rounded-lg">
                <h3 className="text-xl font-bold text-indigo-800 mb-3">🌪️ Turbulent</h3>
                <p className="text-gray-700 mb-2"><strong>Pain:</strong> Engineering changes arrive weekly. Your floor explodes.</p>
                <p className="text-blue-700 mb-2"><strong>QRM:</strong> Short decision lines inside cells. Cut approval chains.</p>
                <p className="text-green-700 font-semibold"><strong>Effect:</strong> Re-sequencing takes 10 minutes, not 10 hours.</p>
              </div>

              {/* Uncertain */}
              <div className="p-6 bg-cyan-50 border border-cyan-200 rounded-lg">
                <h3 className="text-xl font-bold text-cyan-800 mb-3">🎲 Uncertain</h3>
                <p className="text-gray-700 mb-2"><strong>Pain:</strong> Lead times swing wildly. Staffing breaks. Energy costs spike.</p>
                <p className="text-blue-700 mb-2"><strong>QRM:</strong> Plan on time, not capacity. Set WIP caps per step.</p>
                <p className="text-green-700 font-semibold"><strong>Effect:</strong> Flow stays robust. External chaos stays external.</p>
              </div>

              {/* Novel */}
              <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-lg">
                <h3 className="text-xl font-bold text-emerald-800 mb-3">✨ Novel</h3>
                <p className="text-gray-700 mb-2"><strong>Pain:</strong> Customer wants steel frames you've never made.</p>
                <p className="text-blue-700 mb-2"><strong>QRM:</strong> Multi-skill cell tackles it. Use batches of 5 units.</p>
                <p className="text-green-700 font-semibold"><strong>Effect:</strong> Learn fast, fail small. WIP stays contained.</p>
              </div>

              {/* Ambiguous */}
              <div className="p-6 bg-violet-50 border border-violet-200 rounded-lg">
                <h3 className="text-xl font-bold text-violet-800 mb-3">🤷 Ambiguous</h3>
                <p className="text-gray-700 mb-2"><strong>Pain:</strong> Roles overlap everywhere. Nobody owns decisions.</p>
                <p className="text-blue-700 mb-2"><strong>QRM:</strong> Clear ownership inside Q-ROC cells. One person decides.</p>
                <p className="text-green-700 font-semibold"><strong>Effect:</strong> Decisions happen in 5 minutes. Lead time drops hard.</p>
              </div>
            </div>
          </Card>

          {/* QRM Essentials */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">QRM Essentials: Do This Now</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                  <span className="font-medium"><strong>Track</strong> lead time above all else</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                  <span className="font-medium"><strong>Cap</strong> WIP at every step</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                  <span className="font-medium"><strong>Install</strong> POLCA for visible priorities</span>
                </li>
              </ul>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                  <span className="font-medium"><strong>Cross-train</strong> your teams inside cells</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                  <span className="font-medium"><strong>Start</strong> each day with 10-minute stand-ups</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                  <span className="font-medium"><strong>Use</strong> small batches with daily feedback</span>
                </li>
              </ul>
            </div>
          </Card>

          {/* Call to Action */}
          <Card className="p-8 bg-gradient-to-r from-red-600 to-pink-600 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Kill Your Bottlenecks?</h2>
            <p className="text-2xl mb-8">
              What's the one bottleneck you'll kill with a QRM cell—starting next month?
            </p>
            
            <div className="space-x-4">
              <Button 
                size="lg" 
                className="px-8 py-3 bg-white text-red-600 hover:bg-red-50 font-semibold"
                onClick={() => window.location.href = '/business-case'}
              >
                <BarChart3 className="mr-2 h-5 w-5" />
                Calculate Your Lead Time ROI
              </Button>
              <Button 
                variant="outline"
                size="lg" 
                className="px-8 py-3 border-2 border-white text-white hover:bg-white hover:text-red-600 font-semibold"
                onClick={() => window.open('mailto:info@smartqrmnavigator.com?subject=BANI/TUNA Implementation Discussion&body=Hello Jan,\n\nI read your BANI/TUNA framework and want to discuss implementing QRM at our facility.\n\nOur situation:\n• Lead Time: 30 days (target: 15 days)\n• WIP: 1000 units (target: 600 units)\n• OTIF: 80% (target: 95%)\n• Main bottlenecks: Welding, Assembly\n\nI want to start with Steel frames pilot in 30 days.\n\nPlease advise on next steps.\n\nCompany: [Your Company]\nName: [Your Name]\nEmail: [Your Email]\n\nReady to kill our bottlenecks!\n\nBest regards', '_blank')}
              >
                <Award className="mr-2 h-5 w-5" />
                Contact Jan Pauwels Now
              </Button>
            </div>
          </Card>

          {/* Navigation */}
          <div className="text-center mt-12">
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/'}
              className="px-8 py-3"
            >
              ← Back to Home
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BANITUNAPage;
